import React, { useState, useRef, useCallback, useEffect } from 'react';
import Box from '@mui/material/Box';
import { WidgetConfig } from './widget';

interface ResizableWidgetProps {
  widget: WidgetConfig;
  onResize: (widgetId: string, newSize: { w: number; h: number }) => void;
  isEditing: boolean;
  children: React.ReactNode;
}

type ResizeDirection = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

export const ResizableWidget = ({
  widget,
  onResize,
  isEditing,
  children,
}: ResizableWidgetProps) => {
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState<ResizeDirection>('se');
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [initialSize, setInitialSize] = useState({ w: 0, h: 0 });
  const widgetRef = useRef<HTMLDivElement>(null);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent, direction: ResizeDirection) => {
      if (!isEditing) return;

      e.preventDefault();
      e.stopPropagation();

      setIsResizing(true);
      setResizeDirection(direction);
      setDragStart({ x: e.clientX, y: e.clientY });
      setInitialSize({ w: widget.layout.width, h: widget.layout.height });
    },
    [isEditing, widget.layout.width, widget.layout.height]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing) return;

      const deltaX = e.clientX - dragStart.x;
      const deltaY = e.clientY - dragStart.y;

      const gridUnitSize = 100;
      let newWidth = initialSize.w;
      let newHeight = initialSize.h;

      if (resizeDirection.includes('e')) {
        newWidth = Math.max(
          1,
          Math.min(12, Math.round(initialSize.w + deltaX / gridUnitSize))
        );
      }
      if (resizeDirection.includes('w')) {
        newWidth = Math.max(
          1,
          Math.min(12, Math.round(initialSize.w - deltaX / gridUnitSize))
        );
      }
      if (resizeDirection.includes('s')) {
        newHeight = Math.max(
          1,
          Math.min(8, Math.round(initialSize.h + deltaY / gridUnitSize))
        );
      }
      if (resizeDirection.includes('n')) {
        newHeight = Math.max(
          1,
          Math.min(8, Math.round(initialSize.h - deltaY / gridUnitSize))
        );
      }

      if (
        newWidth !== widget.layout.width ||
        newHeight !== widget.layout.height
      ) {
        onResize(widget.id, { w: newWidth, h: newHeight });
      }
    },
    [
      isResizing,
      dragStart,
      initialSize,
      widget.id,
      widget.layout.width,
      widget.layout.height,
      onResize,
      resizeDirection,
    ]
  );

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }

    return undefined;
  }, [isResizing, handleMouseMove, handleMouseUp]);

  const getResizeHandleCursor = (direction: ResizeDirection) => {
    const cursors = {
      n: 'ns-resize',
      s: 'ns-resize',
      e: 'ew-resize',
      w: 'ew-resize',
      ne: 'nesw-resize',
      nw: 'nwse-resize',
      se: 'nwse-resize',
      sw: 'nesw-resize',
    };
    return cursors[direction];
  };

  return (
    <Box
      ref={widgetRef}
      sx={{
        position: 'relative',
        border: isEditing ? '2px dashed #9ca3af' : undefined,
        transition: isEditing ? 'border-color 0.2s' : undefined,
        '&:hover': isEditing ? { borderColor: '#3b82f6' } : undefined,
        gridColumn: `span ${Math.min(widget.layout.width, 12)}`,
        gridRow: `span ${widget.layout.height}`,
      }}
    >
      {children}

      {isEditing && (
        <>
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: 24,
              height: 24,
              bgcolor: 'primary.main',
              opacity: 0.7,
              transition: 'opacity 0.2s',
              zIndex: 20,
              cursor: getResizeHandleCursor('nw'),
              '&:hover': { opacity: 1 },
            }}
            onMouseDown={(e) => handleMouseDown(e, 'nw')}
          />
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: 24,
              height: 24,
              bgcolor: 'primary.main',
              opacity: 0.7,
              transition: 'opacity 0.2s',
              zIndex: 20,
              cursor: getResizeHandleCursor('ne'),
              '&:hover': { opacity: 1 },
            }}
            onMouseDown={(e) => handleMouseDown(e, 'ne')}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              width: 24,
              height: 24,
              bgcolor: 'primary.main',
              opacity: 0.7,
              transition: 'opacity 0.2s',
              zIndex: 20,
              cursor: getResizeHandleCursor('sw'),
              '&:hover': { opacity: 1 },
            }}
            onMouseDown={(e) => handleMouseDown(e, 'sw')}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              width: 48,
              height: 48,
              bgcolor: 'primary.main',
              opacity: 0.7,
              transition: 'opacity 0.2s',
              zIndex: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: getResizeHandleCursor('se'),
              clipPath: 'polygon(100% 0, 0 100%, 100% 100%)',
              '&:hover': { opacity: 1 },
            }}
            onMouseDown={(e) => handleMouseDown(e, 'se')}
          >
            <Box sx={{ color: 'black', fontSize: 12, userSelect: 'none' }}>
              ⤡
            </Box>
          </Box>

          {/* Edge handles */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 24,
              right: 24,
              height: 4,
              bgcolor: 'primary.main',
              opacity: 0.5,
              transition: 'opacity 0.2s',
              zIndex: 20,
              cursor: getResizeHandleCursor('n'),
              '&:hover': { opacity: 1 },
            }}
            onMouseDown={(e) => handleMouseDown(e, 'n')}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 24,
              right: 24,
              height: 4,
              bgcolor: 'primary.main',
              opacity: 0.5,
              transition: 'opacity 0.2s',
              zIndex: 20,
              cursor: getResizeHandleCursor('s'),
              '&:hover': { opacity: 1 },
            }}
            onMouseDown={(e) => handleMouseDown(e, 's')}
          />
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: 24,
              bottom: 24,
              width: 4,
              bgcolor: 'primary.main',
              opacity: 0.5,
              transition: 'opacity 0.2s',
              zIndex: 20,
              cursor: getResizeHandleCursor('w'),
              '&:hover': { opacity: 1 },
            }}
            onMouseDown={(e) => handleMouseDown(e, 'w')}
          />
          <Box
            sx={{
              position: 'absolute',
              right: 0,
              top: 24,
              bottom: 24,
              width: 4,
              bgcolor: 'primary.main',
              opacity: 0.5,
              transition: 'opacity 0.2s',
              zIndex: 20,
              cursor: getResizeHandleCursor('e'),
              '&:hover': { opacity: 1 },
            }}
            onMouseDown={(e) => handleMouseDown(e, 'e')}
          />
        </>
      )}

      {isResizing && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            left: 8,
            bgcolor: 'primary.dark',
            color: 'black',
            px: 1,
            py: 0.5,
            borderRadius: 1,
            fontSize: 12,
            zIndex: 30,
            pointerEvents: 'none',
          }}
        >
          {widget.layout.width}×{widget.layout.height}
        </Box>
      )}
    </Box>
  );
};
